import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, errorText } from '../lib/api';
import { useMe, useSession } from '../lib/session';
import { fmtDate } from '../lib/format';
import { Button, Card, ErrorText, Pill } from '../components/ui';
import { PolicyText } from './Apply';

/**
 * INT-ADM-03: until a member has accepted the current version of every floor document, this screen replaces the app
 * (the API refuses everything else with POLICY_ACCEPTANCE_REQUIRED). A new version published mid-session shows it
 * again as soon as any request is refused.
 */

interface PolicyDoc {
  id: string;
  kind: string;
  label: string;
  version: number;
  title: string;
  body: string;
  publishedAt: string;
}

const KEY = ['me-policies'];
const isGateError = (e: unknown) => e instanceof ApiError && e.status === 403 && e.body.code === 'POLICY_ACCEPTANCE_REQUIRED';

export function PolicyGate({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useMe();
  const { signOut } = useSession();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<{ pending: PolicyDoc[] }>('/me/policies'), staleTime: 60_000, refetchInterval: 5 * 60_000 });
  const [ticked, setTicked] = useState<Record<string, boolean>>({});

  // any refused request means a new version was published: re-check straight away
  useEffect(() => {
    const recheck = (e: unknown) => {
      if (isGateError(e)) void qc.invalidateQueries({ queryKey: KEY });
    };
    const a = qc.getQueryCache().subscribe((ev) => ev.type === 'updated' && recheck(ev.query.state.error));
    const b = qc.getMutationCache().subscribe((ev) => ev.type === 'updated' && recheck(ev.mutation?.state.error));
    return () => {
      a();
      b();
    };
  }, [qc]);

  const accept = useMutation({
    mutationFn: (ids: string[]) => api<{ pending: PolicyDoc[] }>('/me/policies/accept', { body: { policyDocumentIds: ids } }),
    onSuccess: (r) => {
      qc.setQueryData(KEY, r);
      setTicked({});
      if (!r.pending.length) void qc.invalidateQueries({ predicate: (query) => query.queryKey[0] !== KEY[0] });
    },
    onError: () => void qc.invalidateQueries({ queryKey: KEY }),
  });

  if (q.isLoading) return <div className="p-8 text-sub">Loading…</div>;
  const pending = q.data?.pending ?? [];
  if (!pending.length) return <>{children}</>;

  const tz = me.data?.timezone ?? 'UTC';
  const all = pending.every((d) => ticked[d.id]);
  const updated = pending.some((d) => d.version > 1);
  return (
    <div className="flex min-h-screen justify-center px-4 py-10">
      <main className="flex w-full max-w-[760px] flex-col gap-4">
        <div className="flex items-center gap-3 px-1">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#2b4a78] bg-accent-bg font-mono text-sm font-semibold text-accent">VTF</div>
          <div className="flex flex-col">
            <h1 className="m-0 text-xl font-bold">Before you continue</h1>
            <span className="text-sm text-sub">
              {updated ? 'Some floor documents have changed. ' : ''}Please read and accept {pending.length === 1 ? 'this document' : `these ${pending.length} documents`} to use the floor{me.data ? `, ${me.data.name.split(' ')[0]}` : ''}.
            </span>
          </div>
        </div>
        {pending.map((d) => (
          <Card key={d.id} className="flex flex-col">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
              <h2 className="m-0 text-[15px] font-semibold">{d.title}</h2>
              <div className="flex items-center gap-2">
                {d.version > 1 && <Pill tone="warn">Updated</Pill>}
                <span className="text-xs text-muted">
                  {d.label !== d.title ? `${d.label} · ` : ''}version {d.version} · {fmtDate(d.publishedAt, tz)}
                </span>
              </div>
            </div>
            <PolicyText body={d.body} className="max-h-[42vh] overflow-y-auto px-4 py-3" />
            <label className="flex cursor-pointer items-start gap-2.5 border-t border-line px-4 py-3">
              <input type="checkbox" className="mt-0.5 h-[18px] w-[18px] shrink-0" checked={!!ticked[d.id]} onChange={(e) => setTicked({ ...ticked, [d.id]: e.target.checked })} />
              <span>
                I have read and accept the {d.label.toLowerCase()} (version {d.version})
              </span>
            </label>
          </Card>
        ))}
        <ErrorText error={accept.error ? errorText(accept.error) : null} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button type="button" className="cursor-pointer text-sm text-sub hover:text-ink" onClick={() => void signOut()}>
            Sign out
          </button>
          <Button variant="primary" disabled={!all || accept.isPending} onClick={() => accept.mutate(pending.map((d) => d.id))}>
            {accept.isPending ? 'Saving…' : 'Accept and continue'}
          </Button>
        </div>
      </main>
    </div>
  );
}
